import React from "react";
import {
  MessageCircle, RefreshCw, Send, Loader2, Users, ChevronLeft, Pause,
} from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../lib/api";

function fmtTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("da-DK", {
    day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
  });
}

function fmtRelative(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "lige nu";
  if (diff < 3600) return `${Math.floor(diff / 60)} min siden`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} t siden`;
  return `${Math.floor(diff / 86400)} d siden`;
}

export default function DriverChatPanel() {
  const [threads, setThreads] = React.useState([]);
  const [totalUnread, setTotalUnread] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [activeDriver, setActiveDriver] = React.useState(null); // {driver_id, driver_name}
  const [messages, setMessages] = React.useState([]);
  const [driverInfo, setDriverInfo] = React.useState(null);
  const [threadLoading, setThreadLoading] = React.useState(false);
  const [text, setText] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const listRef = React.useRef(null);

  const loadThreads = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi().get("/admin/driver-messages");
      setThreads(data.threads || []);
      setTotalUnread(data.total_unread || 0);
    } catch (e) {
      toast.error("Kunne ikke hente chat-tråde");
    } finally { setLoading(false); }
  }, []);

  const loadThread = React.useCallback(async (driverId) => {
    setThreadLoading(true);
    try {
      const { data } = await adminApi().get(`/admin/driver-messages/${driverId}`);
      setMessages(data.messages || []);
      setDriverInfo(data.driver);
    } catch {
      toast.error("Kunne ikke hente besked-tråd");
    } finally { setThreadLoading(false); }
  }, []);

  React.useEffect(() => { loadThreads(); }, [loadThreads]);
  React.useEffect(() => {
    // Poll every 20s
    const id = setInterval(() => {
      loadThreads();
      if (activeDriver) loadThread(activeDriver.driver_id);
    }, 20000);
    return () => clearInterval(id);
  }, [loadThreads, loadThread, activeDriver]);
  React.useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const openThread = async (t) => {
    setActiveDriver(t);
    await loadThread(t.driver_id);
    // After opening, reload threads to reset unread counter
    setTimeout(loadThreads, 500);
  };

  const send = async () => {
    const t = text.trim();
    if (!t || !activeDriver) return;
    setSending(true);
    try {
      const { data } = await adminApi().post(`/admin/driver-messages/${activeDriver.driver_id}`, { text: t });
      setMessages((m) => [...m, data.message]);
      setText("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke sende");
    } finally { setSending(false); }
  };

  return (
    <div className="space-y-4" data-testid="driver-chat-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-display font-bold text-2xl text-brand-dark flex items-center gap-2">
            <MessageCircle size={22} className="text-brand-red" />
            Chauffør-chat
            {totalUnread > 0 && (
              <span className="bg-brand-red text-white text-xs font-bold rounded-full min-w-[24px] h-[24px] flex items-center justify-center px-2">
                {totalUnread}
              </span>
            )}
          </h2>
          <p className="text-xs text-brand-text2 mt-1">
            Direkte 2-vejs chat med chauffører i felten. Polling hver 20. sek.
          </p>
        </div>
        <button
          onClick={loadThreads}
          className="text-xs border border-brand-dark text-brand-dark hover:bg-brand-dark hover:text-white px-3 py-2 flex items-center gap-1 transition-colors flex-shrink-0"
          data-testid="chat-refresh"
        >
          <RefreshCw size={12} /> Opdater
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 min-h-[500px]">
        {/* Threads list */}
        <div className={`bg-white border border-brand ${activeDriver ? "hidden lg:block" : ""}`} data-testid="chat-threads-list">
          <div className="border-b border-brand p-3 flex items-center gap-2 text-sm font-bold text-brand-dark">
            <Users size={14} /> {threads.length} tråde
          </div>
          {loading && threads.length === 0 && (
            <div className="p-6 text-center text-brand-text2 text-sm">
              <Loader2 size={16} className="animate-spin inline mr-2" /> Indlæser…
            </div>
          )}
          {!loading && threads.length === 0 && (
            <div className="p-6 text-center text-brand-text2 text-sm">
              Ingen chauffør-beskeder endnu.
            </div>
          )}
          <div className="divide-y divide-brand max-h-[600px] overflow-y-auto">
            {threads.map((t) => {
              const isActive = activeDriver?.driver_id === t.driver_id;
              return (
                <button
                  key={t.driver_id}
                  onClick={() => openThread(t)}
                  className={`w-full text-left p-3 transition-colors ${
                    isActive ? "bg-brand-bg" : "hover:bg-brand-bg/50"
                  }`}
                  data-testid={`chat-thread-${t.driver_id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-bold text-sm text-brand-dark truncate">{t.driver_name}</div>
                    {t.unread_from_driver > 0 && (
                      <span className="bg-brand-red text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 flex-shrink-0">
                        {t.unread_from_driver}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-brand-text2 mt-0.5 truncate">
                    {t.last_direction === "admin_to_driver" && <span className="text-emerald-700">Du: </span>}
                    {t.last_text}
                  </div>
                  <div className="text-[10px] text-brand-text2 mt-0.5">{fmtRelative(t.last_at)}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Active thread */}
        <div className={`bg-white border border-brand lg:col-span-2 flex flex-col ${activeDriver ? "" : "hidden lg:flex lg:items-center lg:justify-center"}`} data-testid="chat-active-thread">
          {!activeDriver ? (
            <div className="text-center text-brand-text2 text-sm py-12">
              <MessageCircle size={32} className="mx-auto mb-2 opacity-30" />
              Vælg en chauffør for at se beskeder
            </div>
          ) : (
            <>
              <div className="border-b border-brand p-3 flex items-center gap-2">
                <button
                  onClick={() => setActiveDriver(null)}
                  className="lg:hidden text-brand-text2 hover:text-brand-dark"
                >
                  <ChevronLeft size={20} />
                </button>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm text-brand-dark truncate">{driverInfo?.name || activeDriver.driver_name}</div>
                  <div className="text-xs text-brand-text2 truncate">{driverInfo?.email}</div>
                </div>
                {driverInfo?.is_paused && (
                  <span className="text-xs bg-red-100 text-red-900 border border-red-300 px-2 py-1 font-bold uppercase flex items-center gap-1 flex-shrink-0">
                    <Pause size={11} /> Pauset
                  </span>
                )}
              </div>

              <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-2 max-h-[450px] min-h-[300px] bg-brand-bg/30">
                {threadLoading && messages.length === 0 && (
                  <div className="text-center text-brand-text2 text-sm py-8">
                    <Loader2 size={16} className="animate-spin inline" />
                  </div>
                )}
                {messages.map((m) => {
                  const fromDriver = m.direction === "driver_to_admin";
                  return (
                    <div
                      key={m.id}
                      className={`flex ${fromDriver ? "justify-start" : "justify-end"}`}
                      data-testid={`admin-chat-msg-${m.id}`}
                    >
                      <div className={`max-w-[80%] p-3 text-sm ${
                        fromDriver ? "bg-white border border-brand text-brand-dark" : "bg-brand-red text-white"
                      }`}>
                        <div className="text-[10px] uppercase tracking-widest opacity-60 mb-1">
                          {fromDriver ? "Chauffør" : "Dig (drift)"}
                        </div>
                        <div className="whitespace-pre-wrap break-words">{m.text}</div>
                        <div className="text-[10px] opacity-60 mt-1">{fmtTime(m.created_at)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="border-t border-brand p-3 flex gap-2">
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      send();
                    }
                  }}
                  rows={1}
                  placeholder="Skriv besked til chauffør…"
                  className="flex-1 border border-brand p-2 text-sm focus:outline-none focus:border-brand-dark resize-none"
                  data-testid="admin-chat-input"
                />
                <button
                  onClick={send}
                  disabled={sending || !text.trim()}
                  className="bg-brand-red text-white px-4 hover:bg-red-700 disabled:opacity-60 flex items-center justify-center"
                  data-testid="admin-chat-send"
                >
                  {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
